// src/Payroll_Management/PayRoll_Report/ConveyanceAllowanceReportPrint.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Printer, RefreshCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function toDDMMYYYY(input) {
  const v = s(input);
  if (!v) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [yy, mm, dd] = v.split("-");
    return `${dd}/${mm}/${yy}`;
  }
  return v;
}
function monthName(m) {
  const n = toInt(m);
  const names = [
    "",
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];
  return names[n] || s(m).toUpperCase() || "SELECT";
}
function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return s(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Support both node key styles
function brId(b) {
  return String(b?.fld_Branchid ?? b?.fld_branchid ?? "");
}
function brName(b) {
  return s(b?.fld_BranchName ?? b?.fld_branchname ?? "");
}

const ConveyanceAllowanceReportPrint = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();
  const location = useLocation();

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // querystring
  const q = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const finYearId = s(q.get("finYearId")) || localStorage.getItem("finYearId") || "";
  const loginBranchId =
    s(q.get("loginBranchId")) ||
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    s(q.get("branchType")) ||
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";

  const empCode = s(q.get("empCode"));
  const empName = s(q.get("empName"));
  const branchIdParam = s(q.get("branchId")) || "Select";
  const month = s(q.get("month")) || "Select";
  const year = s(q.get("year")) || "Select";
  const fromDate = s(q.get("fromDate")); // yyyy-mm-dd OR dd/MM/yyyy
  const toDate = s(q.get("toDate"));

  const canViewAll = s(branchType).toUpperCase() === "HO";
  const effectiveBranchId = canViewAll ? branchIdParam : loginBranchId;

  // meta + data
  const [metaLoading, setMetaLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);

  const screenBadge = useMemo(
    () => "INFRA SCREEN : Conveyance Allowance Report (Print)",
    []
  );

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing loginBranchId/branchType. Please re-login.");
      return;
    }

    try {
      setMetaLoading(true);

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/meta`, {
        params: {
          finYearId: toInt(finYearId),
          branchId: toInt(loginBranchId),
          branchType: s(branchType),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setCompanyName(res.data.companyName || "");
      setBranches(res.data.branches || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta error");
    } finally {
      setMetaLoading(false);
    }
  };

  const loadPrintData = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing loginBranchId/branchType. Please re-login.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/conveyance-allowance-report/print-data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: s(loginBranchId),
          branchType: s(branchType),
          branchId: s(effectiveBranchId),
          empCode,
          empName,
          month,
          year,
          fromDate: toDDMMYYYY(fromDate),
          toDate: toDDMMYYYY(toDate),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load print data");
        return;
      }

      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print data error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBranchObj = branches.find((b) => brId(b) === String(effectiveBranchId));
  const branchLabel = brName(selectedBranchObj) || (canViewAll ? "Select" : "Login Branch");

  // If SP returns these, show them
  const hasAccountNo = rows.length > 0 && Object.prototype.hasOwnProperty.call(rows[0], "fld_accountno");
  const hasThroughBank =
    rows.length > 0 &&
    (Object.prototype.hasOwnProperty.call(rows[0], "fld_throughbank") ||
      Object.prototype.hasOwnProperty.call(rows[0], "fld_paymentmode"));

  const totalAmount = useMemo(() => {
    return (rows || []).reduce((sum, r) => {
      const v = Number(r?.fld_conveyanceallowance);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [rows]);

  const onPrint = () => window.print();
  const onBack = () => navigate(-1);
  const onReload = async () => {
    await loadMeta();
    await loadPrintData();
  };

  // total row colspans
  const totalRowCols = 4 + (hasAccountNo ? 1 : 0) + 1 + (hasThroughBank ? 1 : 0); // base cols
  // base header cols:
  // SNo, Code, EmpName, Designation, (AccountNo?), Allowance, (ThroughBank?)

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        {/* NOT printed */}
        <div className="print:hidden">
          <HeaderBar
            title={<span className="font-dmserif text-3xl">CONVEYANCE ALLOWANCE REPORT - PRINT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />

          <div
            className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : `${currentSkin.glass} bg-white text-black border-gray-200`
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="text-sm font-bold">
                <span
                  className={`px-4 py-2 rounded-2xl ${
                    isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {screenBadge}
                </span>
              </div>

              <div className="flex flex-wrap gap-3 justify-end">
                <button onClick={onBack} className={ghostBtn}>
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <button onClick={onReload} className={ghostBtn} disabled={loading || metaLoading}>
                  <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Reload
                </button>

                <button onClick={onPrint} className={primaryBtn} disabled={loading || metaLoading}>
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>

            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              This is a dedicated print page (same tab). Click <b>Print</b>.
            </div>
          </div>
        </div>

        {/* Printable Area */}
        <div className="mx-auto max-w-5xl mt-6">
          <div className="print-area border border-black bg-white text-black">
            {/* header box */}
            <div className="p-4 border-b border-black">
              <div className="flex gap-4 items-start">
                <div className="w-28 h-20 border border-black flex items-center justify-center text-xs">
                  <img
                    src="/assets/company-logo.png"
                    alt="Company Logo"
                    className="max-w-full max-h-full"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <span className="logo-fallback">LOGO</span>
                </div>

                <div className="flex-1">
                  <div className="text-center leading-tight">
                    <div className="text-xl font-extrabold">{companyName || "Company Name"}</div>
                    <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>

                    {/* Optional: Put address/phone from backend later */}
                    <div className="text-xs mt-1">
                      <div>Address Line 1</div>
                      <div>Phone No: xxxxxxxxxx &nbsp;&nbsp; Email: xxxxx@xxx.com</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-black pt-2">
                <div className="text-center font-extrabold text-lg">
                  Conveyance Allowance Report- &nbsp; {monthName(month)} &nbsp; {year || "SELECT"} &nbsp;&nbsp;
                  {s(branchLabel).toUpperCase()}
                </div>
              </div>
            </div>

            {/* table */}
            <div className="p-4">
              <table className="w-full border-collapse" style={{ border: "1px solid #000" }}>
                <thead>
                  <tr>
                    <th className="th">S.No</th>
                    <th className="th">Code</th>
                    <th className="th">Employee Name</th>
                    <th className="th">Designation</th>
                    {hasAccountNo && <th className="th">Account No</th>}
                    <th className="th">Conveyance Allowance</th>
                    {hasThroughBank && <th className="th">Through Bank</th>}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={totalRowCols} className="td text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={totalRowCols} className="td text-center">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={`${r.fld_employeecode}-${idx}`}>
                        <td className="td text-center">{idx + 1}</td>
                        <td className="td text-center">{r.fld_employeecode}</td>
                        <td className="td">{r.fld_employee}</td>
                        <td className="td">{r.fld_designationname}</td>
                        {hasAccountNo && <td className="td text-center">{r.fld_accountno}</td>}
                        <td className="td text-right">{formatMoney(r.fld_conveyanceallowance)}</td>
                        {hasThroughBank && (
                          <td className="td text-center">
                            {r.fld_throughbank ?? r.fld_paymentmode ?? "Through Bank"}
                          </td>
                        )}
                      </tr>
                    ))
                  )}

                  {/* Total row like screenshot */}
                  {rows.length > 0 && (
                    <tr>
                      {/* blank columns until "Total Amount" cell */}
                      <td className="td" colSpan={hasAccountNo ? 4 : 3} />
                      <td className="td font-bold text-right">Total Amount</td>
                      <td className="td font-bold text-right">{formatMoney(totalAmount)}</td>
                      {hasThroughBank && <td className="td" />}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* footer signatures */}
            <div className="p-4 pt-10">
              <div className="flex justify-between text-sm font-bold">
                <div>Prepared By</div>
                <div>GM Finance</div>
                <div>Sr.Mgr Accounts</div>
              </div>
            </div>
          </div>
        </div>

        {/* Print styling */}
        <style>{`
          .print-area { box-shadow: none; }
          .logo-fallback { display:none; }
          .th {
            border: 1px solid #000;
            padding: 8px 6px;
            font-size: 12px;
            font-weight: 700;
            text-align: center;
          }
          .td {
            border: 1px solid #000;
            padding: 8px 6px;
            font-size: 12px;
          }
          @media print {
            .print\\:hidden { display: none !important; }
            body { background: #fff !important; }
            .min-h-screen { padding: 0 !important; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
        `}</style>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default ConveyanceAllowanceReportPrint;
